/**
 * Push — drain the mutation queue to the server.
 *
 * Only runs when the user is authenticated AND online. Each entry is processed
 * in queue order; failures backoff via the queue's attempt counter.
 */

import {
  listPending,
  markInFlight,
  markCompleted,
  markFailed,
  rewriteRecordId,
} from '../db/queue';
import * as sessionsRepo from '../db/sessions';
import {
  syncSession as apiSyncSession,
  updateSession as apiUpdateSession,
  deleteSession as apiDeleteSession,
} from '../api';
import { toApiPayload } from './adapter';

/**
 * Drain the queue. Returns the number of entries processed.
 *
 * In-flight serialisation is enforced upstream by the sync engine's
 * `_running` guard — drainQueue itself doesn't gate, otherwise a manual
 * `requestSync()` overlapping with an auto-sync would silently drop the
 * second pass (the engine sets `_requested` but drainQueue would early-
 * return 0 anyway).
 *
 * @param {Object} opts
 * @param {boolean} opts.isAuthed
 */
export async function drainQueue({ isAuthed }) {
  if (!isAuthed) return 0;
  let processed = 0;
  try {
    const pending = await listPending();
    for (const entry of pending) {
      await markInFlight(entry.id);
      try {
        if (entry.entity === 'session') {
          if (entry.op === 'create') {
            const response = await apiSyncSession(toApiPayload(entry.payload));
            // Server echoes `sessionId`; the local id we sent should match.
            const serverId = response?.data?.sessionId || response?.sessionId || response?.data?.id || response?.id || entry.payload?.id;
            if (serverId && serverId !== entry.recordId) {
              await rewriteRecordId(entry.recordId, serverId);
              // Also update the local Dexie row's id field. Easiest: read the
              // existing row, soft-delete it under the old id, and put a new
              // row under the server id.
              const local = await sessionsRepo.getSession(entry.recordId);
              if (local) {
                // For simplicity we keep client-supplied UUIDs as authoritative
                // (the backend currently accepts them). If the server forces
                // a different id later, the row migration would go here.
              }
            }
            await sessionsRepo.markSynced(entry.recordId);
          } else if (entry.op === 'update') {
            await apiUpdateSession(entry.recordId, entry.payload);
            await sessionsRepo.markSynced(entry.recordId);
          } else if (entry.op === 'delete') {
            await apiDeleteSession(entry.recordId);
          }
        }
        await markCompleted(entry.id);
        processed += 1;
      } catch (err) {
        // 401 means the auth token is gone — bail; the next signedIn event
        // will retry. Don't burn the attempt counter for auth failures.
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          await markFailed(entry.id, err);
          break;
        }
        await sessionsRepo.markSyncError(entry.recordId, err);
        await markFailed(entry.id, err);
      }
    }
  } catch (err) {
    console.warn('[push] drainQueue error:', err?.message || err);
  }
  return processed;
}
